/**
 * ai-explanation i18n — FR default + AR (RTL). Registered once in the module's onRegister. Keys are
 * namespaced `aix.*`; missing keys fall back to FR then the raw key (never a crash, never a blank).
 *
 * The legal/safety disclaimer is config-GOVERNED on the server (server/src/config/integration.config.js)
 * and rides every generate/regenerate result as `disclaimer`; the strings here are the FALLBACK shown
 * before any generation (so the always-on disclaimer is present even on an empty/absent panel) — they
 * mirror the governed wording verbatim.
 */
import { registerMessages } from "@/app";

export function registerAixMessages(): void {
  registerMessages("fr", {
    // nav
    "nav.aix": "Explication IA",
    "nav.aix.console": "Explication IA",

    // console (lookup)
    "aix.console.title": "Explication IA d'un compte-rendu",
    "aix.console.subtitle":
      "Ouvrez l'explication médecin générée par l'IA, à côté d'un rapport signé.",
    "aix.console.lookup.label": "Identifiant du compte-rendu",
    "aix.console.lookup.hint":
      "L'explication n'est disponible que pour un compte-rendu émis (rapport signé).",
    "aix.console.lookup.placeholder": "ex. 10428",
    "aix.console.open": "Ouvrir l'explication",
    "aix.console.empty.title": "Aucun compte-rendu sélectionné",
    "aix.console.empty.desc":
      "Saisissez l'identifiant d'un compte-rendu émis pour consulter ou générer son explication IA.",

    // panel
    "aix.panel.title": "Explication IA",
    "aix.panel.subtitle": "Compte-rendu {cr}",
    "aix.panel.back": "Retour",
    "aix.panel.for": "Pour le compte-rendu",
    "aix.panel.model": "Modèle",
    "aix.panel.generatedNote":
      "Explication à destination du médecin. Le biologiste reste seul responsable de l'interprétation.",

    // bilingual block
    "aix.block.title": "Explication bilingue",
    "aix.block.fr": "Français",
    "aix.block.ar": "العربية",
    "aix.block.copy": "Copier le texte",
    "aix.block.copied": "Texte copié.",

    // disclaimer (always-on) — mirrors the governed server wording (integration.config.js)
    "aix.disclaimer.title": "Avertissement",
    "aix.disclaimer.fr":
      "Cette explication est générée par une IA à titre informatif pour le médecin ; elle ne remplace pas l'interprétation du biologiste ni l'avis du prescripteur.",
    "aix.disclaimer.ar":
      "هذا الشرح مُولَّد بواسطة الذكاء الاصطناعي لأغراض إعلامية للطبيب؛ وهو لا يحل محل تفسير البيولوجي أو رأي الطبيب المُحيل.",

    // actions
    "aix.action.generate": "Générer l'explication",
    "aix.action.regenerate": "Régénérer",
    "aix.action.generating": "Génération en cours…",
    "aix.action.regenerating": "Régénération en cours…",
    "aix.generated.ok": "Explication générée.",
    "aix.regenerated.ok": "Explication régénérée.",

    // none-yet (no explanation persisted, generation possible)
    "aix.none.title": "Aucune explication pour l'instant",
    "aix.none.desc":
      "Aucune explication IA n'a encore été générée pour ce compte-rendu. Lancez la génération pour produire un texte bilingue à destination du médecin.",

    // indisponible (graceful degrade — disabled / error from the never-throws service)
    "aix.indispo.title": "Explication indisponible",
    "aix.indispo.disabled":
      "Le service d'explication IA n'est pas configuré sur cette installation. Le compte-rendu signé reste pleinement consultable.",
    "aix.indispo.error":
      "L'explication IA n'a pas pu être produite pour le moment. Le compte-rendu signé n'est jamais bloqué — réessayez plus tard.",
    "aix.indispo.notEmitted":
      "L'explication n'est disponible que pour un compte-rendu émis (rapport signé). Émettez d'abord le compte-rendu.",
    "aix.indispo.quota":
      "Le quota quotidien d'explications IA est atteint pour ce laboratoire. Réessayez demain.",
    "aix.indispo.retry": "Réessayer la génération",

    // not-available (cloud-only module unmounted on this install)
    "aix.unavailable.title": "Non disponible sur cette installation",
    "aix.unavailable.desc":
      "L'explication IA est un service cloud. Il n'est pas activé sur cette installation locale ; le compte-rendu signé reste pleinement disponible.",

    // misc
    "aix.na": "—",
  });

  registerMessages("ar", {
    "nav.aix": "شرح بالذكاء الاصطناعي",
    "nav.aix.console": "شرح بالذكاء الاصطناعي",

    "aix.console.title": "شرح تقرير بالذكاء الاصطناعي",
    "aix.console.subtitle": "افتح الشرح الموجَّه للطبيب بجانب تقرير موقَّع.",
    "aix.console.lookup.label": "معرّف التقرير",
    "aix.console.lookup.hint": "الشرح متاح فقط لتقرير صادر (تقرير موقَّع).",
    "aix.console.lookup.placeholder": "مثال 10428",
    "aix.console.open": "فتح الشرح",
    "aix.console.empty.title": "لم يتم اختيار أي تقرير",
    "aix.console.empty.desc": "أدخل معرّف تقرير صادر لعرض شرحه بالذكاء الاصطناعي أو توليده.",

    "aix.panel.title": "شرح بالذكاء الاصطناعي",
    "aix.panel.subtitle": "التقرير {cr}",
    "aix.panel.back": "رجوع",
    "aix.panel.for": "للتقرير",
    "aix.panel.model": "النموذج",
    "aix.panel.generatedNote": "شرح موجَّه للطبيب. يبقى البيولوجي وحده مسؤولاً عن التفسير.",

    "aix.block.title": "شرح ثنائي اللغة",
    "aix.block.fr": "Français",
    "aix.block.ar": "العربية",
    "aix.block.copy": "نسخ النص",
    "aix.block.copied": "تم نسخ النص.",

    "aix.disclaimer.title": "تنبيه",
    "aix.disclaimer.fr":
      "Cette explication est générée par une IA à titre informatif pour le médecin ; elle ne remplace pas l'interprétation du biologiste ni l'avis du prescripteur.",
    "aix.disclaimer.ar":
      "هذا الشرح مُولَّد بواسطة الذكاء الاصطناعي لأغراض إعلامية للطبيب؛ وهو لا يحل محل تفسير البيولوجي أو رأي الطبيب المُحيل.",

    "aix.action.generate": "توليد الشرح",
    "aix.action.regenerate": "إعادة التوليد",
    "aix.action.generating": "جارٍ التوليد…",
    "aix.action.regenerating": "جارٍ إعادة التوليد…",
    "aix.generated.ok": "تم توليد الشرح.",
    "aix.regenerated.ok": "تمت إعادة توليد الشرح.",

    "aix.none.title": "لا يوجد شرح بعد",
    "aix.none.desc":
      "لم يُولَّد أي شرح بالذكاء الاصطناعي لهذا التقرير بعد. ابدأ التوليد لإنتاج نص ثنائي اللغة موجَّه للطبيب.",

    "aix.indispo.title": "الشرح غير متاح",
    "aix.indispo.disabled":
      "خدمة الشرح بالذكاء الاصطناعي غير مُعدَّة على هذا التثبيت. يبقى التقرير الموقَّع متاحًا بالكامل.",
    "aix.indispo.error":
      "تعذّر إنتاج الشرح بالذكاء الاصطناعي حاليًا. لا يُحظر التقرير الموقَّع أبدًا — حاول لاحقًا.",
    "aix.indispo.notEmitted": "الشرح متاح فقط لتقرير صادر (موقَّع). أصدِر التقرير أولًا.",
    "aix.indispo.quota": "تم بلوغ الحصة اليومية للشروح في هذا المختبر. حاول غدًا.",
    "aix.indispo.retry": "إعادة محاولة التوليد",

    "aix.unavailable.title": "غير متاح على هذا التثبيت",
    "aix.unavailable.desc":
      "الشرح بالذكاء الاصطناعي خدمة سحابية، وهي غير مُفعَّلة على هذا التثبيت المحلي؛ يبقى التقرير الموقَّع متاحًا بالكامل.",

    "aix.na": "—",
  });
}
